// Fraud and scam intelligence layer

export interface FraudSignal {
  type: FraudType;
  severity: "low" | "medium" | "high" | "critical";
  description: string;
  action: "flag" | "warn" | "block";
}

export type FraudType =
  | "suspicious_domain"
  | "counterfeit_risk"
  | "fake_discount"
  | "seller_decay"
  | "fake_reviews"
  | "bait_switch"
  | "dropship_disguise"
  | "price_anomaly";

interface SellerProfile {
  name: string;
  domain?: string;
  price: number;
  originalPrice?: number;
  reviewCount?: number;
  rating?: number;
  accountAge?: number; // months
  returnRate?: number; // percentage
}

const SUSPICIOUS_TLD = [".xyz", ".top", ".buzz", ".club", ".site", ".online", ".store"];
const KNOWN_TRUSTED = ["amazon.com", "walmart.com", "bestbuy.com", "target.com", "costco.com", "ebay.com", "newegg.com"];

export function analyzeFraudSignals(seller: SellerProfile, categoryMedianPrice: number): FraudSignal[] {
  const signals: FraudSignal[] = [];

  // Suspicious domain
  if (seller.domain) {
    const isTrusted = KNOWN_TRUSTED.some(d => seller.domain!.includes(d));
    if (!isTrusted && SUSPICIOUS_TLD.some(tld => seller.domain!.endsWith(tld))) {
      signals.push({
        type: "suspicious_domain",
        severity: "high",
        description: `Seller domain ${seller.domain} uses a high-risk TLD`,
        action: "warn",
      });
    }
  }

  // Price anomaly (too good to be true)
  if (seller.price < categoryMedianPrice * 0.4) {
    signals.push({
      type: "price_anomaly",
      severity: "high",
      description: `Price is ${Math.round((1 - seller.price / categoryMedianPrice) * 100)}% below market median`,
      action: "warn",
    });
  }

  // Fake discount detection
  if (seller.originalPrice && seller.price) {
    const discount = (seller.originalPrice - seller.price) / seller.originalPrice;
    if (discount > 0.6) {
      signals.push({
        type: "fake_discount",
        severity: "medium",
        description: `${Math.round(discount * 100)}% discount is suspiciously high`,
        action: "flag",
      });
    }
  }

  // Seller decay (new account, low reviews)
  if (seller.accountAge !== undefined && seller.accountAge < 3 && seller.reviewCount !== undefined && seller.reviewCount < 10) {
    signals.push({
      type: "seller_decay",
      severity: "medium",
      description: "New seller with minimal track record",
      action: "flag",
    });
  }

  // Fake review patterns
  if (seller.rating !== undefined && seller.reviewCount !== undefined) {
    if (seller.rating >= 4.9 && seller.reviewCount > 100) {
      signals.push({
        type: "fake_reviews",
        severity: "medium",
        description: "Unusually high rating with many reviews suggests review manipulation",
        action: "flag",
      });
    }
  }

  // High return rate
  if (seller.returnRate !== undefined && seller.returnRate > 15) {
    signals.push({
      type: "bait_switch",
      severity: "medium",
      description: `${seller.returnRate}% return rate suggests product mismatch`,
      action: "warn",
    });
  }

  return signals;
}

export function getFraudRiskLevel(signals: FraudSignal[]): "safe" | "caution" | "risky" | "dangerous" {
  const criticals = signals.filter(s => s.severity === "critical").length;
  const highs = signals.filter(s => s.severity === "high").length;
  const mediums = signals.filter(s => s.severity === "medium").length;

  if (criticals > 0) return "dangerous";
  if (highs >= 2) return "dangerous";
  if (highs >= 1) return "risky";
  if (mediums >= 2) return "caution";
  if (mediums >= 1) return "caution";
  return "safe";
}

export function getFraudSummary(signals: FraudSignal[]): string | null {
  if (signals.length === 0) return null;
  const worst = signals.sort((a, b) => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return order[a.severity] - order[b.severity];
  })[0];
  return worst.description;
}
